<script setup>
import { ref, computed, watch } from 'vue'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import CartPaymentScreenPaymentMethods from './CartPaymentScreenPaymentMethods.vue'
import { confirmPaymentWithExistingMethod } from '../helpers/payments'
import { useCartStore } from '../stores/cartStore'
import { captureError } from '../services/sentry'

const cartStore = useCartStore()
const props = defineProps({
  clientSecret: {
    type: String,
    required: true
  },
  paymentMethods: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['edit', 'delete', 'add'])

const isProcessing = ref(false)
const errorMessage = ref('')

// Create validation schema
const validationSchema = yup.object({
  payment_method: yup.string().required('Please select a payment method')
})

// Setup form with vee-validate's useForm
const { setFieldValue, handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    payment_method: ''
  }
})

// Watch for payment methods and set default when available
watch(
  () => props.paymentMethods,
  (methods) => {
    if (methods.length === 1) {
      // If there's only one payment method, select it
      setFieldValue('payment_method', methods[0].id)
    } else if (methods.length > 1) {
      // If multiple methods, find and select the default one
      const defaultMethod = methods.find((method) => method.isDefault)
      if (defaultMethod) {
        setFieldValue('payment_method', defaultMethod.id)
      }
    }
  },
  { immediate: true }
)

const onSubmit = handleSubmit(async (values) => {
  if (!props.clientSecret) {
    const error = new Error('Missing payment intent. Please try again.')
    errorMessage.value = error.message
    captureError(error, {
      type: 'payment_intent_missing',
      cartConfig: cartStore.cartData?.cartConfig
    })
    return
  }

  isProcessing.value = true
  errorMessage.value = ''

  try {
    const { error, paymentIntent } = await confirmPaymentWithExistingMethod(
      props.clientSecret,
      values.payment_method,
      cartStore
    )

    if (error) {
      errorMessage.value = error.message
      captureError(error, {
        type: 'payment_method_confirmation_error',
        cartConfig: cartStore.cartData?.cartConfig
      })
      return
    }

    // Payment successful - redirect to success page
    // window.location.href = `/cart/success/?payment_intent=${paymentIntent.id}`
  } catch (e) {
    console.error('Error processing payment:', e)
    errorMessage.value = 'An unexpected error occurred. Please try again.'
    captureError(e, {
      type: 'payment_method_unexpected_error',
      cartConfig: cartStore.cartData?.cartConfig
    })
  } finally {
    isProcessing.value = false
  }
})
</script>

<template>
  <form @submit.prevent="onSubmit">
    <CartPaymentScreenPaymentMethods
      :payment-methods="paymentMethods"
      name="payment_method"
    />
    <div v-if="errorMessage" class="alert alert-soft-danger mb-4">
      {{ errorMessage }}
    </div>
    <div class="d-grid">
      <button
        type="submit"
        class="btn btn-lg btn-primary"
        :disabled="isProcessing"
      >
        <span
          v-if="isProcessing"
          class="spinner-border spinner-border-sm me-2"
          role="status"
        ></span>
        {{ isProcessing ? 'Processing...' : 'Submit & Pay Now' }}
      </button>
    </div>
  </form>
</template>
